import { omit, merge, pick, set } from 'lodash';
import { addVersion, mergeVersionStrings, nowInMicroseconds } from '../util/wbo-utils';
import type {
    WboAnnotationDocumentData,
    WboAnnotationDocumentMetadata,
    WboDelta,
    WboMsg,
    WboBatchedBroadcastMsg,
    WboChildPointData,
    WboAnyAnnotationData,
    WboStraightLineData,
    WboTranslationData,
} from './wbo-annotation.model';

// Type declaration for structuredClone (available in Node.js 17+ and modern browsers)
declare global {
    function structuredClone<T>(value: T): T;
}

/**
 * Represents a Wbo annotations document in memory.
 */
export default class WboDocument {
    private documentData: WboAnnotationDocumentData;

    constructor(documentData?: WboAnnotationDocumentData) {
        if (documentData && typeof documentData !== 'object') {
            throw new Error('Invalid document data provided to WboDocument constructor');
        }

        this.documentData =
            documentData ||
            ({
                metadata: { versionId: 0, appliedVersions: '0' },
            } as WboAnnotationDocumentData);
    }

    setData(data: WboAnnotationDocumentData): void {
        this.documentData = data;
    }

    getData(): WboAnnotationDocumentData {
        return this.documentData;
    }

    getVersion(): number {
        return this.documentData?.metadata?.versionId ?? 0;
    }

    /**
     * Sets the version of the document. Also applies that version to the applied versions list.
     */
    setVersion(version: number): void {
        if (version === undefined) {
            return;
        }
        if (version > (this.documentData?.metadata?.versionId ?? 0)) {
            set(this.documentData, 'metadata.versionId', version);
        }

        set(
            this.documentData,
            'metadata.appliedVersions',
            addVersion(this.documentData?.metadata?.appliedVersions ?? '0', version)
        );
    }

    /**
     * Applies a version string to the document. This is used to keep track of the versions that have been applied to the
     * document. The version string is a comma-separated list of version numbers or ranges (e.g. "1,3-5,7").
     */
    applyVersionString(versionString: string): void {
        set(
            this.documentData,
            'metadata.appliedVersions',
            mergeVersionStrings(this.documentData?.metadata?.appliedVersions ?? '0', versionString)
        );
    }

    setOtherMetadata(metadata: WboAnnotationDocumentMetadata): void {
        const { appliedVersions, versionId, ...otherMetadata } = metadata || {};
        if (!this.documentData.metadata) {
            this.documentData.metadata = { versionId: 0, appliedVersions: '0' };
        }
        Object.keys(otherMetadata).forEach((key) => {
            const typedKey = key as keyof typeof otherMetadata;
            const value = otherMetadata[typedKey];
            if (this.documentData.metadata) {
                (this.documentData.metadata as Record<string, unknown>)[key] = value;
            }
        });
    }

    /**
     * Applies an operation to the document data. Returns the operation that was applied, in a form that the WBO drawer
     * adapter can understand.
     */
    applyOperation(op: WboDelta): WboDelta {
        if (op.opType === 'broadcast') {
            const opMsg = op.msg as WboMsg;
            switch (opMsg?.data?.type) {
                case 'delete':
                    this.applyDeleteAnnotation(op);
                    break;
                case 'update':
                    this.applyUpdateAnnotation(op);
                    break;
                case 'child':
                    this.applyChildAnnotation(op);
                    break;
                case 'line':
                case 'straight':
                    this.applyAddAnnotation(op);
                    break;
                case 'finish':
                    break;
                default:
                    throw new Error(`Unsupported message type "${opMsg?.data?.type}" for WBO operation "${op.opType}"`);
            }
            // Return an efficient clone of the original op
            return this.deepClone(op);
        } else if (op.opType === 'removeAllByUser' || op.opType === 'removeForUpload') {
            const opMsg = op.msg as WboMsg;
            const deletedAnnotations = this.applyRemoveAllByUser(op);
            return {
                ...op,
                msg: {
                    board: op.msg.board,
                    clientId: op.msg.clientId,
                    elementsToDelete: deletedAnnotations,
                    operationId: opMsg.operationId,
                    userName: op.msg.userName,
                },
            };
        } else if (op.opType === 'removeAllForUpload') {
            const opMsg = op.msg as WboMsg;
            const deletedAnnotations = this.applyRemoveAll(op);
            return {
                ...op,
                msg: {
                    board: op.msg.board,
                    clientId: op.msg.clientId,
                    elementsToDelete: deletedAnnotations,
                    operationId: opMsg.operationId,
                    userName: op.msg.userName,
                },
            };
        } else if (op.opType === 'broadcastMultiple') {
            const opMsg = op.msg as WboBatchedBroadcastMsg;
            // Recursively apply all operations in the batch
            if (opMsg.data) {
                for (const groupedMessage of opMsg.data) {
                    this.applyOperation({
                        opType: 'broadcast',
                        msg: {
                            board: op.msg.board,
                            clientId: op.msg.clientId,
                            operationId: groupedMessage.operationId ?? nowInMicroseconds(),
                            userName: op.msg.userName,
                            data: groupedMessage,
                        },
                        v: op.v,
                    });
                }
            }
            // Return a clone of the batched operation
            return this.deepClone(op);
        } else {
            throw new Error(`Unsupported WBO operation: "${op.opType}"`);
        }
    }

    /**
     * Resets the document to its initial state.
     */
    reset(): void {
        this.documentData = { metadata: { versionId: 0 } } as WboAnnotationDocumentData;
    }

    private applyDeleteAnnotation(op: WboDelta): void {
        if (op.v !== undefined) {
            this.setVersion(op.v);
        }
        const msg = op.msg as WboMsg;
        if (msg.data?.id) {
            delete this.documentData[msg.data.id];
        }
    }

    private applyAddAnnotation(op: WboDelta): void {
        if (op.v !== undefined) {
            this.setVersion(op.v);
        }
        const msg = op.msg as WboMsg;
        if (!msg.data?.id) {
            return;
        }
        const id = msg.data.id;
        if (op.msg.data) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            this.documentData[id] = omit(this.deepClone(op.msg.data) as any, [
                'socket',
                'boardName',
            ]) as WboAnyAnnotationData;
            // Optimize children array
            const annotation = this.documentData[id] as WboAnyAnnotationData;
            if (annotation && Array.isArray((annotation as { _children?: unknown[] })._children)) {
                (annotation as { _children: WboChildPointData[] })._children = (
                    annotation as { _children: WboChildPointData[] }
                )._children.map((child: WboChildPointData) => this.cloneChildPointData(child));
            }
        }
    }

    private applyUpdateAnnotation(op: WboDelta): void {
        if (op.v !== undefined) {
            this.setVersion(op.v);
        }
        const msg = op.msg as WboMsg;
        if (!msg.data?.id) {
            return;
        }
        const annotation = this.documentData[msg.data.id];
        if (annotation && op.msg.data) {
            merge(
                annotation as WboAnyAnnotationData,
                pick(op.msg.data as WboTranslationData & WboStraightLineData, ['deltax', 'deltay', 'x2', 'y2'])
            ); // Only update updatable fields
        }
    }

    private applyChildAnnotation(op: WboDelta): boolean {
        let parentId = (op?.msg?.data as WboChildPointData).parent;
        if (!parentId) {
            return false;
        }
        const parent = this.documentData[parentId];
        if (!parent) {
            return false;
        }
        if (op.v !== undefined) {
            this.setVersion(op.v);
        }
        let child = this.cloneChildPointData(op.msg.data as WboChildPointData);
        if (Array.isArray((parent as { _children?: WboChildPointData[] })._children)) {
            let previousChild = (parent as { _children: WboChildPointData[] })._children[
                (parent as { _children: WboChildPointData[] })._children.length - 1
            ] as WboChildPointData;
            if (previousChild?.x == child?.x && previousChild?.y == child?.y) {
                return true;
            }
            (parent as { _children: WboChildPointData[] })._children.push(child);
        } else {
            (parent as { _children: WboChildPointData[] })._children = [child];
        }
        return true;
    }

    /**
     * Removes all user board annotations in the board, with the following rules:
     * If the message contains isHost = true, all annotations are deleted.
     * Otherwise, if isOwner = true, only the annotation owned by the specified userName are deleted.
     * Otherwise, both anonymous and annotations owned by the specified userName are deleted.
     */
    private applyRemoveAllByUser(op: WboDelta): string[] {
        if (op.v !== undefined) {
            this.setVersion(op.v);
        }
        let msg = op.msg as WboMsg;
        const elementsToDelete = msg.elementsToDelete || [];
        elementsToDelete.push(...this.getElementsToDelete(msg));
        elementsToDelete.forEach((key) => delete this.documentData[key]);
        return elementsToDelete;
    }

    /**
     * Removes all annotations.
     */
    private applyRemoveAll(op: WboDelta): string[] {
        if (op.v !== undefined) {
            this.setVersion(op.v);
        }
        const elementsToDelete = [];
        for (const key in this.documentData) {
            if (key === 'metadata') {
                continue;
            }
            elementsToDelete.push(key);
            delete this.documentData[key];
        }
        return elementsToDelete;
    }

    /**
     * Returns the elements to delete based on the removeAllByUser rules.
     */
    private getElementsToDelete(msg: WboMsg): string[] {
        if (!msg || (!msg.isHost && !msg.isOwner && !msg.userName)) {
            return [];
        }
        const elementsToDelete: string[] = [];
        for (const key in this.documentData) {
            if (key === 'metadata') {
                continue;
            }
            const annotation = this.documentData[key] as WboAnyAnnotationData;
            if (!annotation) {
                continue;
            }
            if (msg.isHost) {
                // Deletes all
                elementsToDelete.push(key);
            } else if (msg.isOwner) {
                if (annotation.userName === msg.userName || !annotation.userName) {
                    // Deletes own and anonymous annotations
                    elementsToDelete.push(key);
                }
            } else if (annotation.userName === msg.userName) {
                // Deletes own annotations only
                elementsToDelete.push(key);
            }
        }
        return elementsToDelete;
    }

    /**
     * Efficient deep cloning utility that uses structuredClone when available,
     * falls back to JSON serialization for compatibility.
     */
    private deepClone<T>(obj: T): T {
        if (typeof structuredClone !== 'undefined') {
            return structuredClone(obj);
        }
        // Fallback for older environments
        return JSON.parse(JSON.stringify(obj));
    }

    /**
     * Optimized cloning for child point data that only copies necessary fields.
     */
    private cloneChildPointData(data: WboChildPointData): WboChildPointData {
        return {
            parent: data.parent,
            tool: data.tool,
            type: data.type,
            x: data.x,
            y: data.y,
        };
    }
}
